import type { NostrEvent } from '@nostrify/nostrify';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { NoteContent } from '@/components/NoteContent';
import { useAuthor } from '@/hooks/useAuthor';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { genUserName } from '@/lib/genUserName';

interface NoteCardProps {
  event: NostrEvent;
}

export function NoteCard({ event }: NoteCardProps) {
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;

  const displayName = metadata?.name || genUserName(event.pubkey);
  const avatarUrl = metadata?.picture;

  const timestamp = new Date(event.created_at * 1000).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  // Handle reposts
  const isRepost = event.kind === 6 || event.kind === 16;
  let repostedEvent: NostrEvent | null = null;

  if (isRepost && event.kind === 6) {
    try {
      repostedEvent = JSON.parse(event.content) as NostrEvent;
    } catch {
      // Invalid repost
    }
  }

  return (
    <Card className="hover:shadow-lg transition-shadow duration-300">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={avatarUrl} alt={displayName} />
            <AvatarFallback>{displayName[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm truncate">{displayName}</p>
            <p className="text-xs text-muted-foreground">{timestamp}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isRepost && repostedEvent ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground italic">Reposted</p>
            <NoteCard event={repostedEvent} />
          </div>
        ) : (
          <div className="whitespace-pre-wrap break-words text-sm">
            <NoteContent event={event} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
